/** 门户「积分使用」流水文案 */

const GEN_TYPE_LABELS = {
  VIDEO: '视频消耗',
  IMAGE: '图片消耗',
  AUDIO: '音频消耗',
  TTS: '配音消耗',
  TEXT: '文本消耗',
  PROMPT: '提示词优化',
  LLM: '模型消耗'
}

function looksLikeUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(value || '').trim())
}

function isPointsToken(value) {
  return /^\d+\s*积分$/.test(String(value || '').trim())
}

export function getLedgerTaskId(row) {
  const id = row?.entityId || row?.relatedTaskId
  if (!id) return null
  const trimmed = String(id).trim()
  return looksLikeUuid(trimmed) ? trimmed : null
}

/** 同一 relatedTaskId 已有 CONSUME 时隐藏 FREEZE；未结算 FREEZE 保留 */
export function collapseSettledFreezeRows(rows) {
  if (!rows?.length) return rows || []
  const settledTaskIds = new Set()
  for (const row of rows) {
    if (String(row.transactionType || '').toUpperCase() !== 'CONSUME') continue
    const taskId = getLedgerTaskId(row)
    if (taskId) settledTaskIds.add(taskId)
  }
  if (!settledTaskIds.size) return rows
  return rows.filter((row) => {
    if (String(row.transactionType || '').toUpperCase() !== 'FREEZE') return true
    const taskId = getLedgerTaskId(row)
    return !taskId || !settledTaskIds.has(taskId)
  })
}

export function humanizeCreditsDescription(raw, relatedTaskId) {
  const text = String(raw || '').trim()
  if (!text) return null

  const streaming = text.match(/^流式生成任务(?:完成|失败)?[:：]\s*([0-9a-f-]{8,})/i)
  if (streaming) {
    return { title: '提示词优化', description: `任务 ${streaming[1]}` }
  }

  const parts = text.split(/\s*·\s*/).map((part) => part.trim()).filter(Boolean)
  if (!parts.length) return null
  const typeKey = parts[0].toUpperCase()
  const typeLabel = GEN_TYPE_LABELS[typeKey] || (/生成$/.test(parts[0]) ? parts[0] : null)
  if (!typeLabel) return null
  const taskIdFromDesc = parts.slice(1).find((part) => looksLikeUuid(part))
  const relatedId = relatedTaskId && looksLikeUuid(String(relatedTaskId)) ? String(relatedTaskId).trim() : null
  const taskId = taskIdFromDesc || relatedId
  const provider = parts.slice(1).find((part) => !looksLikeUuid(part) && !isPointsToken(part))
  return {
    title: typeLabel,
    description: taskId ? `任务 ${taskId}` : (provider || '')
  }
}

/** 从 lotAllocations 推断主积分池 */
function poolFromAllocations(meta) {
  const list = meta?.lotAllocations
  if (!Array.isArray(list) || !list.length) return null
  let task = 0
  let recharge = 0
  let membership = 0
  for (const row of list) {
    if (!row || typeof row !== 'object') continue
    const pt = String(row.pointType || '').toUpperCase()
    const amt = Math.abs(Number(row.amount) || 0)
    if (pt === 'CHECKIN' || pt === 'TASK' || pt === 'TASK_CREDIT') task += amt
    else if (pt === 'UNIVERSAL' || pt === 'RECHARGE') recharge += amt
    else if (pt === 'MEMBERSHIP') membership += amt
  }
  const hit = [task > 0, recharge > 0, membership > 0].filter(Boolean).length
  if (hit === 0) return null
  if (hit > 1) return 'mixed'
  if (task > 0) return 'task'
  if (membership > 0) return 'membership'
  return 'recharge'
}

/** 流水归属池：任务 / 充值 / 会员 */
export function ledgerRowPool(row) {
  if (!row || typeof row !== 'object') return 'unknown'
  const type = String(row.transactionType || '').toUpperCase()
  const desc = String(row.description || '')
  const pointType = String(row.pointType || row.meta?.pointType || '').toUpperCase()
  if (pointType === 'CHECKIN' || pointType === 'TASK' || pointType === 'TASK_CREDIT') return 'task'
  if (pointType === 'UNIVERSAL' || pointType === 'RECHARGE') return 'recharge'
  if (pointType === 'MEMBERSHIP') return 'membership'
  if (pointType === 'MIXED') return 'mixed'
  const fromAlloc = poolFromAllocations(row.meta)
  if (fromAlloc) return fromAlloc
  if (type === 'EXPIRE') return 'task'
  if (/签到|任务积分|每日进入|任务领取|完善个人|首次|邀请|好友创作|会员每周|每日对话|每日图片|每日视频/i.test(desc)) {
    return 'task'
  }
  if (/充值|支付|礼包|兑换|后台赠送|后台调整/i.test(desc)) return 'recharge'
  // 任务领取入账多为 TOPUP，无支付关键词时按任务池
  if (type === 'TOPUP') return 'task'
  return 'unknown'
}

/** 合并类型列：任务积分 / 充值积分 / 任务积分消耗 / 充值积分消耗 … */
export function ledgerTypeLabel(typeOrRow) {
  const row = typeOrRow && typeof typeOrRow === 'object' ? typeOrRow : null
  if (!row) {
    const key = String(typeOrRow || '').toUpperCase()
    if (key === 'TOPUP') return '充值积分'
    if (key === 'CONSUME') return '积分消耗'
    if (key === 'UNFREEZE') return '回退'
    if (key === 'FREEZE' || key === 'HOLDING') return '预扣中'
    if (key === 'CANCELLED') return '已取消'
    if (key === 'EXPIRE') return '任务积分过期'
    return key || '其他'
  }
  const desc = String(row.description || '')
  if (/后台(?:调整|赠送)/.test(desc)) return '赠送'
  const key = String(row.transactionType || '').toUpperCase()
  const pool = ledgerRowPool(row)
  if (key === 'UNFREEZE') return '回退'
  if (key === 'FREEZE' || key === 'HOLDING') {
    return /智能体|创作会话|会话预冻|Agent 会话/.test(desc) ? '已占用' : '预扣中'
  }
  if (key === 'CANCELLED') return '已取消'
  if (key === 'EXPIRE') return '任务积分过期'
  if (key === 'CONSUME') {
    if (pool === 'task') return '任务积分消耗'
    if (pool === 'recharge') return '充值积分消耗'
    if (pool === 'membership') return '会员积分消耗'
    if (pool === 'mixed') return '混合积分消耗'
    // 无池信息时仍避免笼统「消费」，提示需按任务/充值区分
    return '积分消耗'
  }
  if (key === 'TOPUP' || key === 'GIFT' || key === 'TRANSFER_IN') {
    if (pool === 'recharge') return '充值积分'
    if (pool === 'membership') return '会员积分'
    return '任务积分'
  }
  return key || '其他'
}

export function formatCreditsLedgerCopy(row, tagOf) {
  const type = String(row?.transactionType || '').toUpperCase()
  const tag = typeof tagOf === 'function' ? tagOf(type) : ledgerTypeLabel(row)
  const raw = String(row?.description || '').trim()
  const pretty = humanizeCreditsDescription(raw, row?.entityId)
  if (pretty) {
    return {
      title: pretty.title,
      description: pretty.description && pretty.description !== pretty.title ? pretty.description : '',
      tag
    }
  }
  return {
    title: raw || tag,
    description: '',
    tag
  }
}
